import React, { useEffect, useRef, useState, useCallback } from 'react';
import useGoogleMaps from '../../hooks/useGoogleMaps';
import useMapWithLocation from '../../hooks/useMapWithLocation';
import { getCategoryConfig } from '../../config/categories';
import { getLucideMarkerIcon } from '../../utils/lucideToSvg';
import './MapViewer.css';

const MapViewer = ({
  mapData,
  markers = [],
  userLocation = null,
  showUserLocation = true,
  onMarkerClick = null,
  onViewportChange = null,
  height = '400px',
  zoom = 16,
}) => {
  const mapRef = useRef(null);
  const { isLoaded, loadError } = useGoogleMaps();
  const [map, setMap] = useState(null);
  const mapMarkersRef = useRef([]);
  const currentInfoWindowRef = useRef(null);
  const hasFitBoundsRef = useRef(false);

  const {
    userLocation: hookUserLocation,
    isLoadingLocation,
    updateUserMarkerOnMap,
    cleanupMarkers: cleanupLocationMarkers,
  } = useMapWithLocation({
    enableTracking: showUserLocation,
    updateInterval: 3000,
    minDistanceChange: 3,
    showAccuracyCircle: true,
    highAccuracy: true,
  });

  const activeUserLocation = userLocation || hookUserLocation;

  // When onMarkerClick is provided (public viewer), bottom sheet handles detail.
  // Info windows are disabled so they don't double up.
  const useInfoWindows = !onMarkerClick;

  const closeCurrentInfoWindow = useCallback(() => {
    if (currentInfoWindowRef.current) {
      currentInfoWindowRef.current.close();
      currentInfoWindowRef.current = null;
    }
  }, []);

  const getMarkerIcon = useCallback((category) => {
    const config = getCategoryConfig(category);
    return getLucideMarkerIcon(config.icon, config.color, 24);
  }, []);

  const createInfoWindowContent = useCallback((markerData) => {
    const config = getCategoryConfig(markerData.category);

    const dietaryBadges = Object.entries(markerData.dietaryOptions || {})
      .filter(([, v]) => v)
      .map(([key]) => {
        const labels = {
          vegetarian: '🥬 Vegetarian', vegan: '🌱 Vegan',
          glutenFree: '🌾 Gluten-Free', kosher: '✡️ Kosher', halal: '☪️ Halal',
        };
        return `<span class="dietary-badge">${labels[key] || key}</span>`;
      }).join('');

    const allergenBadges = (markerData.allergens || []).map(a => {
      const allergenMap = {
        gluten: '🌾 Gluten', nuts: '🥜 Tree Nuts', peanuts: '🥜 Peanuts',
        dairy: '🥛 Dairy', eggs: '🥚 Eggs', soy: '🫘 Soy',
        fish: '🐟 Fish', shellfish: '🦐 Shellfish', sesame: '🌰 Sesame',
      };
      return `<span class="allergen-badge">⚠️ ${allergenMap[a] || a}</span>`;
    }).join('');

    return `
      <div class="info-window">
        <h3 class="info-title">${markerData.name}</h3>
        ${markerData.description ? `<p class="info-description">${markerData.description}</p>` : ''}
        ${markerData.openHours ? `<p class="info-hours"><strong>⏰ Hours:</strong> ${markerData.openHours}</p>` : ''}
        <p class="info-category" style="color: ${config.color}">
          <strong>📂 Category:</strong> ${config.emoji} ${config.label}
        </p>
        ${dietaryBadges ? `<div class="dietary-options"><strong>✅ Dietary:</strong><br>${dietaryBadges}</div>` : ''}
        ${allergenBadges ? `<div class="allergen-options"><strong>⚠️ Contains:</strong><br>${allergenBadges}</div>` : ''}
        ${markerData.tags?.length ? `<div class="tags"><strong>🏷️ Tags:</strong><br>${markerData.tags.map(t => `<span class="tag">#${t}</span>`).join('')}</div>` : ''}
      </div>
    `;
  }, []);

  const clearMarkers = useCallback(() => {
    closeCurrentInfoWindow();
    mapMarkersRef.current.forEach(m => { if (m?.setMap) m.setMap(null); });
    mapMarkersRef.current = [];
  }, [closeCurrentInfoWindow]);

  const drawAreaBoundary = useCallback((googleMap, coordinates) => {
    try {
      const path = coordinates.map(coord => ({ lat: coord[1], lng: coord[0] }));
      new window.google.maps.Polygon({
        paths: path,
        strokeColor: '#2B8EE8',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#2B8EE8',
        fillOpacity: 0.07,
        map: googleMap,
      });
    } catch (_) {}
  }, []);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapData || !mapRef.current) return;
    try {
      const center = mapData.area?.center
        ? { lat: mapData.area.center.lat, lng: mapData.area.center.lng }
        : { lat: 32.0858, lng: 34.7823 };

      const googleMap = new window.google.maps.Map(mapRef.current, {
        center,
        zoom,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        ],
      });

      setMap(googleMap);

      googleMap.addListener('click', closeCurrentInfoWindow);

      if (onViewportChange) {
        googleMap.addListener('idle', () => {
          const c = googleMap.getCenter();
          onViewportChange({
            centerLat: c.lat(),
            centerLng: c.lng(),
            zoomLevel: googleMap.getZoom(),
          });
        });
      }

      if (mapData.area?.coordinates) {
        drawAreaBoundary(googleMap, mapData.area.coordinates);
      }
    } catch (_) {}
  }, [isLoaded, mapData, zoom, drawAreaBoundary, closeCurrentInfoWindow, onViewportChange]);

  // Add markers
  useEffect(() => {
    if (!map) return;
    clearMarkers();
    if (markers.length === 0) return;

    const newMarkers = [];

    markers.forEach(markerData => {
      let position;
      if (Array.isArray(markerData.coordinates)) {
        position = { lat: markerData.coordinates[1], lng: markerData.coordinates[0] };
      } else if (markerData.coordinates?.lat) {
        position = { lat: markerData.coordinates.lat, lng: markerData.coordinates.lng };
      } else return;

      try {
        const marker = new window.google.maps.Marker({
          position,
          map,
          title: markerData.name,
          icon: getMarkerIcon(markerData.category),
          optimized: false,
        });

        marker.addListener('click', () => {
          if (onMarkerClick) {
            // Bottom sheet mode — just fire the callback, no info window
            closeCurrentInfoWindow();
            onMarkerClick(markerData);
          } else {
            // Dashboard/admin mode — use info windows
            closeCurrentInfoWindow();
            const iw = new window.google.maps.InfoWindow({
              content: createInfoWindowContent(markerData),
            });
            iw.open(map, marker);
            currentInfoWindowRef.current = iw;
            iw.addListener('closeclick', () => { currentInfoWindowRef.current = null; });
          }
        });

        newMarkers.push(marker);
      } catch (_) {}
    });

    mapMarkersRef.current = newMarkers;

    // Fit bounds once on initial load
    if (newMarkers.length > 0 && !hasFitBoundsRef.current) {
      setTimeout(() => {
        const bounds = new window.google.maps.LatLngBounds();
        newMarkers.forEach(m => bounds.extend(m.getPosition()));
        if (mapData.area?.coordinates) {
          mapData.area.coordinates.forEach(c => bounds.extend({ lat: c[1], lng: c[0] }));
        }
        map.fitBounds(bounds);
        hasFitBoundsRef.current = true;
      }, 300);
    }
  }, [map, markers, mapData, onMarkerClick, getMarkerIcon, createInfoWindowContent, clearMarkers, closeCurrentInfoWindow]);

  // User location marker
  useEffect(() => {
    if (!map || !showUserLocation) return;
    if (activeUserLocation) updateUserMarkerOnMap(map);
    return () => cleanupLocationMarkers();
  }, [map, showUserLocation, activeUserLocation, updateUserMarkerOnMap, cleanupLocationMarkers]);

  if (loadError) {
    return (
      <div className="map-error" style={{ height }}>
        <div className="error-content">
          <h3>Map Loading Error</h3>
          <p>{loadError}</p>
          <small>Check your Google Maps API key</small>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="map-loading" style={{ height }}>
        <div className="loading-content">
          <div className="loading-spinner" />
          <p>Loading map…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="map-container" style={{ height }}>
      <div ref={mapRef} className="google-map" style={{ width: '100%', height: '100%' }} />

      {/* Only show legend when not in public viewer mode (no onMarkerClick) */}
      {!onMarkerClick && markers.length > 0 && (
        <div className="map-legend">
          <h4>Legend ({markers.length})</h4>
          <div className="legend-items">
            {[...new Set(markers.map(m => m.category))].map(category => {
              const config = getCategoryConfig(category);
              const IconComponent = config.icon;
              return (
                <div key={category} className="legend-item">
                  <IconComponent color={config.color} size={18} strokeWidth={2} />
                  <span className="legend-label">{config.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapViewer;